import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import { DotWaterhole } from "@/components/AboriginalPatterns";
import { TrendingUp } from "lucide-react";
import PullQuote from "@/components/PullQuote";

const SectionBadge = ({ icon: Icon, label, color = "primary" }: { icon: any; label: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-2xl bg-${color}/8 border border-${color}/15`}>
      <Icon size={16} className={`text-${color}`} />
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] text-${color}`}>{label}</span>
  </div>
);

const StatCard = ({ stat, source }: { stat: string; source: string }) => (
  <div className="glass-card rounded-xl p-5 md:p-6">
    <p className="text-foreground/90 text-base leading-relaxed mb-3">{stat}</p>
    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider">{source}</p>
  </div>
);

const ProblemSection = () => (
  <section className="relative section-padding overflow-hidden">
    <Parallax className="absolute top-10 right-0" offset={-50} offsetX={15}>
      <DotWaterhole className="text-rust opacity-8" size={350} />
    </Parallax>
    <div className="max-w-3xl mx-auto px-6">
      <FadeIn>
        <SectionBadge icon={TrendingUp} label="The Problem" color="rust" />
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 tracking-tight">
          Most Acquisitions Underperform.<br />
          <span className="text-muted-foreground">The Risk Was Already There.</span>
        </h2>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="grid gap-4 mb-12">
          <StatCard
            stat="Roughly 70% of mergers and acquisitions fail to deliver their intended value creation goals."
            source="McKinsey & Company, Harvard Business Review"
          />
          <StatCard
            stat="Up to 67% of strategies fail due to poor execution, not flawed design."
            source="Harvard Business Review, Kaplan &amp; Norton"
          />
          <StatCard
            stat="Cultural and leadership breakdown consistently rank among the primary contributors to deal underperformance."
            source="Deloitte, KPMG M&A Research"
          />
        </div>
      </FadeIn>
      <FadeIn delay={0.2}>
        <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
          <p className="text-foreground font-medium text-xl">
            Integration does not create execution risk.
          </p>
          <p className="text-gradient font-semibold text-xl">
            It reveals it.
          </p>
          <p>
            By the time your team begins diligence, the leadership habits inside the target
            are already set. The question is whether you can see them clearly enough to price the risk.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>
);

export default ProblemSection;
